import { forwardRef, useState, type InputHTMLAttributes } from "react";
import { cn } from "../lib/cn";
import { Eye, EyeOff } from "lucide-react";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, label, hint, error, id, type, ...rest },
  ref,
) {
  const [showPassword, setShowPassword] = useState(false);
  const inputId = id ?? rest.name;
  const isPassword = type === "password";

  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-gray-900">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          id={inputId}
          ref={ref}
          type={isPassword && showPassword ? "text" : type}
          className={cn(
            "block w-full rounded-lg border bg-white px-3.5 py-2.5 text-sm transition-all duration-200",
            "placeholder:text-gray-400/80",
            "focus:outline-none focus:ring-3",
            error
              ? "border-red-300 focus:border-red-500 focus:ring-red-100"
              : "border-gray-200 focus:border-brand-500 focus:ring-brand-500/10",
            "disabled:cursor-not-allowed disabled:bg-gray-50/50 disabled:text-gray-400",
            isPassword && "pr-10",
            className,
          )}
          {...rest}
        />
        {isPassword && (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 focus:outline-none cursor-pointer"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
      </div>
      {error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : hint ? (
        <p className="text-sm text-gray-500">{hint}</p>
      ) : null}
    </div>
  );
});

